import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import {
  BarChart3, PieChart, TrendingUp, Activity, Users, Download,
  ArrowRight, Gauge, Timer, MessageSquare
} from 'lucide-react';

const metrics = [
  { icon: Gauge, title: 'Compliance Score', desc: 'Overall call compliance percentage against your SOP criteria with section-by-section breakdowns.' },
  { icon: TrendingUp, title: 'Trend Analysis', desc: 'Weekly and monthly scoring trends to identify systemic improvements or regressions over time.' },
  { icon: Users, title: 'Agent Performance', desc: 'Compare individual agent scores, call volumes, and improvement trajectories side by side.' },
  { icon: PieChart, title: 'Score Distribution', desc: 'Visualize how your calls distribute across scoring bands from critical failures to top performers.' },
  { icon: Activity, title: 'Daily Activity', desc: 'Track daily call uploads and assessments to understand operational patterns and workload.' },
  { icon: Timer, title: 'Credit Usage', desc: 'Monitor credit consumption patterns to optimize spending and plan future credit purchases.' },
];

const reportFeatures = [
  {
    icon: BarChart3,
    title: 'Interactive Dashboards',
    desc: 'Recharts-powered visualizations with pie charts, line graphs, bar charts, and area charts. Filter by date, team, and agent.',
  },
  {
    icon: Download,
    title: 'Export & Share',
    desc: 'Export reports as CSV for offline analysis. Share insights with stakeholders through customizable report views.',
  },
  {
    icon: MessageSquare,
    title: 'Assessment Details',
    desc: 'Drill into any assessment for a full transcript, section-by-section scoring, violation highlights, and AI recommendations.',
  },
];

const ReportingPage = () => {
  return (
    <div className="bg-[#050505] min-h-screen text-white" data-testid="reporting-page">
      <PublicNav />

      {/* Hero */}
      <section className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-4">Reporting & Analytics</p>
              <h1
                className="text-5xl md:text-6xl font-medium tracking-tighter leading-[1.1] mb-6"
                style={{ fontFamily: "'Clash Display', sans-serif" }}
              >
                Data that{' '}
                <span className="bg-gradient-to-r from-[#FF3366] via-[#FF8C00] to-[#FFD700] bg-clip-text text-transparent">
                  drives action
                </span>
              </h1>
              <p className="text-lg text-zinc-400 leading-relaxed" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Rich, interactive dashboards give you full visibility into call quality, agent performance, and compliance trends. No more spreadsheet reviews.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
            >
              <div className="rounded-3xl overflow-hidden border border-white/5">
                <img
                  src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=60"
                  alt="Analytics dashboard"
                  className="w-full h-72 lg:h-80 object-cover opacity-80"
                  loading="lazy"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Metrics Grid */}
      <section className="py-24 bg-[#0F0F11]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-4">What You Can Track</p>
            <h2
              className="text-4xl md:text-5xl font-medium tracking-tight"
              style={{ fontFamily: "'Clash Display', sans-serif" }}
            >
              Your call center control room
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {metrics.map((m, i) => {
              const Icon = m.icon;
              return (
                <motion.div
                  key={m.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08, duration: 0.5 }}
                  className="rounded-3xl bg-[#1A1A1D] border border-white/5 p-8 group hover:-translate-y-1 transition-transform"
                  data-testid={`metric-card-${i}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FF3366]/20 to-[#FF8C00]/20 flex items-center justify-center mb-5">
                    <Icon size={24} className="text-[#FF8C00]" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{m.title}</h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">{m.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Report Features */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2
              className="text-4xl md:text-5xl font-medium tracking-tight"
              style={{ fontFamily: "'Clash Display', sans-serif" }}
            >
              Built for insight, not just data
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {reportFeatures.map((rf, i) => {
              const Icon = rf.icon;
              return (
                <motion.div
                  key={rf.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.12, duration: 0.5 }}
                  className="text-center"
                >
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#FF3366]/10 to-[#FF8C00]/10 flex items-center justify-center mx-auto mb-5">
                    <Icon size={32} className="text-[#FF3366]" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{rf.title}</h3>
                  <p className="text-sm text-zinc-400 leading-relaxed max-w-xs mx-auto">{rf.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Dashboard Preview */}
      <section className="py-24 bg-[#0F0F11]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="rounded-3xl bg-[#1A1A1D] border border-white/5 p-8 lg:p-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
              {[
                { label: 'Total Calls Processed', value: '12,847' },
                { label: 'Avg Compliance Score', value: '84.2%' },
                { label: 'Active Agents', value: '156' },
                { label: 'Assessments This Month', value: '3,291' },
              ].map((s, i) => (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="rounded-2xl bg-white/5 border border-white/5 p-5 text-center"
                >
                  <p className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-[#FF3366] to-[#FFD700] bg-clip-text text-transparent">
                    {s.value}
                  </p>
                  <p className="text-xs text-zinc-500 mt-1">{s.label}</p>
                </motion.div>
              ))}
            </div>
            <p className="text-center text-zinc-500 text-sm">
              Sample dashboard metrics — your actual data will populate in real-time after setup.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <h2
            className="text-4xl md:text-5xl font-medium tracking-tight mb-4"
            style={{ fontFamily: "'Clash Display', sans-serif" }}
          >
            Ready to see your data?
          </h2>
          <p className="text-zinc-400 text-lg max-w-xl mx-auto mb-8">
            Sign up and start tracking call quality from day one.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/login?tab=register"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#FF3366] to-[#FF8C00] text-white px-8 py-3.5 font-semibold hover:scale-105 transition-transform shadow-[0_0_20px_rgba(255,51,102,0.3)]"
              data-testid="reporting-cta-signup"
            >
              Get Started <ArrowRight size={18} />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 text-white px-8 py-3.5 font-semibold hover:bg-white/10 transition-all"
              data-testid="reporting-cta-demo"
            >
              Request Demo
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default ReportingPage;
